import { logger } from './logger';

/**
 * 缓存管理工具
 */
class CacheManager {
  constructor() {
    this.version = '1.0.0'; // 缓存版本号
    this.tableDetailVersion = '1.0.1'; // 表详情缓存版本号，用于强制刷新
  }

  /**
   * 生成缓存键
   * @param {string} key - 基础键名
   * @param {string} version - 版本号（可选）
   * @returns {string} 带版本的缓存键
   */
  getCacheKey(key, version = this.version) {
    return `${key}_v${version}`;
  }

  /**
   * 生成表详情缓存键
   * @param {string} tableId - 表ID
   * @returns {string} 带版本的表详情缓存键
   */
  getTableDetailCacheKey(tableId) {
    return `erp_table_detail_${tableId}_v${this.tableDetailVersion}`;
  }

  /**
   * 获取缓存数据
   * @param {string} key - 缓存键
   * @param {number} duration - 缓存有效期（毫秒）
   * @returns {any} 缓存数据，如果不存在或已过期则返回 null
   */
  get(key, duration) {
    const cacheKey = this.getCacheKey(key);
    const cachedData = localStorage.getItem(cacheKey);
    if (!cachedData) return null;

    try {
      const { data, timestamp } = JSON.parse(cachedData);
      const now = Date.now();
      if (now - timestamp > duration) {
        this.remove(key);
        return null;
      }
      return data;
    } catch (error) {
      logger.error({ cache: '解析失败', key, error }, 'CacheManager');
      this.remove(key);
      return null;
    }
  }

  /**
   * 设置缓存数据
   * @param {string} key - 缓存键
   * @param {any} data - 要缓存的数据
   * @param {string} version - 版本号（可选）
   */
  set(key, data, version = this.version) {
    const cacheKey = this.getCacheKey(key, version);
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  /**
   * 设置表详情缓存数据
   * @param {string} tableId - 表ID
   * @param {any} data - 要缓存的数据
   */
  setTableDetail(tableId, data) {
    const cacheKey = this.getTableDetailCacheKey(tableId);
    const cacheData = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  /**
   * 移除缓存
   * @param {string} key - 缓存键
   * @param {string} version - 版本号（可选）
   */
  remove(key, version = this.version) {
    const cacheKey = this.getCacheKey(key, version);
    localStorage.removeItem(cacheKey);
  }

  /**
   * 移除表详情缓存
   * @param {string} tableId - 表ID
   */
  removeTableDetail(tableId) {
    const cacheKey = this.getTableDetailCacheKey(tableId);
    localStorage.removeItem(cacheKey);
  }

  /**
   * 强制刷新表详情缓存（通过更新版本号）
   */
  forceRefreshTableDetail() {
    this.tableDetailVersion = `1.${Date.now()}`; // 使用时间戳作为版本号
  }

  /**
   * 清理所有缓存
   */
  clear() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('_v')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * 清理指定前缀的缓存
   * @param {string} prefix - 缓存键前缀
   */
  clearByPrefix(prefix) {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * 设置虚拟客服登录信息缓存
   * @param {string} tenantId - 租户ID
   * @param {Object} loginInfo - 登录信息
   */
  setVirtualServiceLoginInfo(tenantId, loginInfo) {
    const cacheKey = `virtual_service_login_${tenantId}`;
    const cacheData = {
      data: loginInfo,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }

  /**
   * 获取虚拟客服登录信息缓存
   * @param {string} tenantId - 租户ID
   * @param {number} duration - 缓存有效期（毫秒，默认8小时）
   * @returns {Object|null} 登录信息，如果不存在或已过期则返回 null
   */
  getVirtualServiceLoginInfo(tenantId, duration = 8 * 60 * 60 * 1000) {
    const cacheKey = `virtual_service_login_${tenantId}`;
    const cachedData = localStorage.getItem(cacheKey);
    if (!cachedData) return null;

    try {
      const { data, timestamp } = JSON.parse(cachedData);
      const now = Date.now();
      if (now - timestamp > duration) {
        this.removeVirtualServiceLoginInfo(tenantId);
        return null;
      }
      return data;
    } catch (error) {
      logger.error('[CacheManager] 解析虚拟客服登录信息失败:', { tenantId, error });
      this.removeVirtualServiceLoginInfo(tenantId);
      return null;
    }
  }

  /**
   * 移除虚拟客服登录信息缓存
   * @param {string} tenantId - 租户ID
   */
  removeVirtualServiceLoginInfo(tenantId) {
    const cacheKey = `virtual_service_login_${tenantId}`;
    localStorage.removeItem(cacheKey);
  }

  /**
   * 清理所有虚拟客服登录信息缓存
   */
  clearVirtualServiceLoginInfo() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('virtual_service_login_')) {
        localStorage.removeItem(key);
      }
    });
  }
}

export const cacheManager = new CacheManager();
